
import React, { useEffect, useState } from "react";
import { useAssistantData } from "@/hooks/useAssistantData";
import { useChat } from "@/hooks/useChat";
import { useDebugMode } from "@/hooks/useDebugMode";
import ChatHeader from "@/components/ChatHeader";
import ChatInput from "@/components/ChatInput";
import ChatContainer from "@/components/ChatContainer";
import { toast } from "@/components/ui/use-toast";
import ErrorMessage from "@/components/message/ErrorMessage";
import DebugModeToggle from "@/components/DebugModeToggle";

interface ChatProps {
  urlSlug?: string;
}

const Chat: React.FC<ChatProps> = ({ urlSlug = "midnight-hotel" }) => {
  const { assistantData, isLoading: isLoadingAssistantData, connectionError, notFoundError } = useAssistantData(urlSlug);
  const [showError, setShowError] = useState<boolean>(false);
  const isDebugMode = useDebugMode();
  
  const { 
    messages, 
    isProcessing, 
    handleSendMessage 
  } = useChat(assistantData);
  
  // Set showSuggestions to true so the initial suggested questions appear
  const showSuggestions = messages.length === 0;

  // Show a toast notification if there's a connection error
  useEffect(() => {
    if (connectionError) {
      toast({
        title: "Database Connection Error",
        description: `Unable to fetch assistant data for slug: ${urlSlug}. The assistant will use default data.`,
        variant: "destructive",
      });
      setShowError(true);
    }
    
    if (notFoundError) {
      toast({
        title: "Assistant Not Found",
        description: `No assistant found with ID: ${urlSlug}. Using default assistant data.`,
        variant: "default",
      });
      setShowError(true);
    }
  }, [connectionError, notFoundError, urlSlug]);

  return (
    <div className="flex flex-col h-screen bg-midnight-background dark:bg-midnight-dark">
      <ChatHeader assistantData={assistantData} />
      
      {/* Show any specific connection or not found errors */}
      {connectionError && showError && (
        <div className="px-4 pt-4">
          <ErrorMessage errorType="connection_error" />
        </div>
      )}
      
      {notFoundError && showError && (
        <div className="px-4 pt-4">
          <ErrorMessage errorType="assistant_not_found" />
        </div>
      )}
      
      <ChatContainer
        messages={messages}
        isProcessing={isProcessing}
        showSuggestions={showSuggestions}
        onSuggestedQuestionClick={handleSendMessage}
        onSendMessage={handleSendMessage}
        assistantData={assistantData}
        isDebugMode={isDebugMode}
      />
      
      <ChatInput onSendMessage={handleSendMessage} isProcessing={isProcessing} />
      
      {/* Debug Mode Toggle - always included but only visible in Lovable */}
      <DebugModeToggle />
    </div>
  );
};

export default Chat;
